import Link from "next/link";
import Head from "next/head";
import NextScript from "next/script";

import { routes } from "src/shared/routes";
import { useRouterExt } from "src/components/reusable/hooks/useRouterExt";

import type { TTeamConfig } from "src/shared/types/teamConfig.types";
import { useTeamConfig } from "src/components/reusable/components/TeamConfigProvider";

export const FAQS: TTeamConfig["LANGUAGES"]["FAQS"] = [
  {
    question: "Which games are included with the Hawks 3-Game Draft Pack?",
    answer:
      "Upper Level Seats are guaranteed for 3 games in the Hawks 3-Game Draft Pack: Mavericks, Spurs, and a select opponent of your choice! You are able to choose the opponent of your choice during the checkout process. Once purchased you can review your schedule under Upcoming Events on your homepage.",
  },
  {
    question: "Where are my tickets?",
    answer:
      "Your tickets will be made available to you in August 2025 alongside the game dates & times.  You will be sent a text message notifying you when tickets are ready to be managed from your Atlanta Hawks Account Manager.",
  },
  {
    question: "Where will my seats be located?",
    answer:
      "All seats are located in the Upper Level. You will receive a text message in August 2025 when seats are made available on your account.",
  },
  {
    question: "Can these tickets be transferred to a friend?",
    answer:
      "Please follow the rules in your Atlanta Hawks Account Manager specific to ticket transfer rules & functionality.",
  },
  {
    question: "Can my seats be changed?",
    answer:
      "Once tickets have been sent to your account, they cannot be changed. Please contact the State Farm Arena box office for any assistance you may require.",
  },
  {
    question: "Can I link tickets with other pass holders?",
    answer:
      "You can absolutely create a group to link with other pack holders! Navigate to the My Group tab in your menu and copy the invite. This will ensure you are seated next to or close enough to hand a drink to one another for the game.",
  },
  {
    question: "Can I receive a refund if I cannot attend a game?",
    answer: "We do not issue any refunds for games you are not able to attend.",
  },
  {
    question: "How can I get a refund and cancel my pass?",
    answer: "A purchase for the Hawks 3-Game Draft Pack is not refundable.",
  },
];

export const TERMS_AND_CONDITION = () => (
  <>
    By purchasing I agree to receive SMS alerts regarding ticket delivery and
    agree to the{" "}
    <Link
      target="_blank"
      href="https://cdn.prod.website-files.com/66b3d5184e88b53f24e71360/6836259e2aac5657874b9955_Attend%20Terms%20of%20Use%20052425.pdf"
    >
      Terms of Use
    </Link>{" "}
    and{" "}
    <Link
      target="_blank"
      href="https://cdn.prod.website-files.com/66b3d5184e88b53f24e71360/683625a812daf6ceadcbe532_Attend%20Privacy%20Policy%20052725.pdf"
    >
      Privacy Policy
    </Link>
    .
  </>
);

export const PASS_PURCHASE_SUCCESS_MESSAGE: TTeamConfig["LANGUAGES"]["PASS_PURCHASE_SUCCESS_MESSAGE"] =
  {
    TITLE: () => (
      <>
        You&apos;e all set to experience the action! Don&apos;t forget to
        confirm attendance here on game day!
      </>
    ),
    DESCRIPTION: () => {
      const router = useRouterExt();
      return (
        <>
          Friends don&apos;t leave friends behind.{" "}
          <b>
            <u>
              <span
                onClick={() => router.pushSchema(routes.my_group)}
                style={{ cursor: "pointer" }}
              >
                Invite your friends
              </span>
            </u>
          </b>{" "}
          and enjoy games together.
        </>
      );
    },
  };

export const AnalyticsHeader = () => {
  const { TEAM_SCHEMA } = useTeamConfig();

  return (
    <Head>
      <NextScript
        async
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        src="https://www.googletagmanager.com/gtag/js?id=G-MG3ZLEC1VE"
      />

      <script
        id={`${TEAM_SCHEMA}-analytics-inline`}
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        dangerouslySetInnerHTML={{
          __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag() {
                dataLayer.push(arguments);
              }
              gtag('js', new Date());
              gtag('config', 'G-MG3ZLEC1VE');
          `,
        }}
      />
    </Head>
  );
};
